import { Button, Col, Modal, Row } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import { useSelector } from 'react-redux';
import { useOutletContext } from 'react-router-dom';
import { AddCircle, CloseIcon } from '../../../assets/icons';
import BankAccountModal from '../../../components/LinkBankAccount/BankAccountModal';
import PaymentItem from '../../../components/PaymentItem/PaymentItem';
import { Images } from '../../../config/images';
import useGetUserProfile from '../../../Hooks/useGetUserProfile';
import { deleteFundingResource, getFundingSources, makePrimerKey } from '../../../services/AccountApi/paymentMethodApi';
import { NOTIFICATION_MESSAGES } from '../../../utils/constant';
import { getBankLogo } from '../../../utils/getBankLogo';
import { toastNotify } from '../../../utils/toastNotify';
import './Payment.scss';
const Payment = () => {
  const { setLoading } = useOutletContext();
  const shouldLog = useRef(true);
  const [deleteModalData, setDeleteModalData] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [bankModalOpen, setBankModalOpen] = useState(false);
  const [linkTokenUnverified, setLinkTokenUnverified] = useState(null);
  const [verificationStatus, SetVerificationStatus] = useState(null);
  // const [f]= useState(null);

  const [bankData, setBankData] = useState([]);
  const userData = useSelector((state) => state.userData);

  useGetUserProfile();

  const handleFeatchFundingDetails = async () => {
    setLoading(true);
    try {
      const userData = await getFundingSources();
      if (!userData.isError) {
        setBankData(userData?.data.banks);
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (shouldLog.current) {
      shouldLog.current = false;
      handleFeatchFundingDetails();
    }
  }, []);

  const handleOpenDeletModal = (data) => {
    const { sourceType, brandName, lastFourDigit, id } = data;
    setDeleteModalData({
      type: sourceType,
      logo: getBankLogo(brandName),
      name: brandName,
      number: '****' + lastFourDigit,
      id: id,
    });
  };

  const handleAddBankAccount = (data) => {
    if (!data?.data) return;
    setBankData((prevItem) => [...prevItem, data.data]);
  };

  const handleUpdateBanksList = (data) => {
    setBankData([...(data?.data?.banks ?? null)]);
  };

  const handleDeleteBankAccount = async () => {
    setDeleteLoading(true);
    try {
      const userData = await deleteFundingResource(deleteModalData.id);
      if (!userData.isError) {
        let updatedBankArray = bankData.filter((item) => item.id !== deleteModalData.id && item);
        setBankData(updatedBankArray);
        await handleFeatchFundingDetails();
        toastNotify('success', userData?.message);
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setDeleteLoading(false);
      setDeleteModalData(null);
    }
  };

  const handleMakePrimaryCard = async (id) => {
    setLoading(true);
    try {
      const userData = await makePrimerKey(id);
      if (!userData.isError) {
        let updatedBankArray = bankData.map((item) => (item.id === id ? { ...item, isPrimarySource: true } : { ...item, isPrimarySource: false }));
        setBankData(updatedBankArray);
      } else {
        toastNotify('error', userData?.message);
      }
    } catch (error) {
      const { response } = error;
      toastNotify('error', response?.data?.message);
    } finally {
      setLoading(false);
    }
  };

  const bankCount = (bankData && bankData.length) || 0;
  const isSingleAccount = bankCount === 1 ? true : false;

  return (
    <div className='payment'>
      <h4 className='mb-24 payment__title'>Payment Methods</h4>
      <div className='payment__addnote'>
        <LazyLoadImage src={Images.addPaymentMethod} alt='A girl holding moneybag' />
        <p>Link a bank account to potentially receive dividends and purchase shares.</p>
      </div>
      <div className='payment__type payment__type--bank'>
        <p className='subhead-3'>Bank Accounts</p>
        <p className='mb-16'>Purchase shares and transfer funds from Realbricks directly to your bank.</p>
        {bankData.length > 0 && (
          <div className='payment__items'>
            {bankData.length > 0 &&
              bankData.map((data) => (
                <PaymentItem
                  key={data?.id}
                  logo={getBankLogo(data.brandName, '')}
                  name={data.brandName}
                  number={data.lastFourDigit}
                  id={data.id}
                  type={'Account'}
                  isPrimary={data.isPrimarySource}
                  openDeleteModal={() => handleOpenDeletModal(data)}
                  handleMakePrimary={(value) => handleMakePrimaryCard(value)}
                  isSingleAccount={isSingleAccount}
                  bankVerificationStatus={data?.bankVerificationStatus}
                  setBankModalOpen={(value) => {
                    SetVerificationStatus({ bankVerificationStatus: data?.bankVerificationStatus, fundingSourceId: data?.id });
                    setBankModalOpen(value);
                  }}
                  getLinkToken={(value) => {
                    setLinkTokenUnverified(value);
                  }}
                />
              ))}
          </div>
        )}

        <span
          role='button'
          className='cursor-pointer fw-600 text-terracotta'
          onClick={() => {
            if (!userData?.userData?.isDwollaVerified) {
              toastNotify('error', NOTIFICATION_MESSAGES.WARN_DWOLLA_PENDING);
              return;
            }

            if (bankCount >= 6) {
              toastNotify('error', 'User can not add more than 6 bank accounts.');
            } else {
              userData?.userData?.isKYCVerified
                ? setBankModalOpen(true) || setLinkTokenUnverified(null)
                : !userData?.userData?.isKYCVerified
                ? toastNotify('error', 'Please complete all your verification steps first.')
                : toastNotify('error', 'Your account is still in-review, you will be informed after completion of your account review.');
            }
          }}
        >
          <AddCircle size='20' verticalAlign='-4px' marginRight='10px' />
          Add new
        </span>
      </div>

      {/* Modal For Delete Account and Card */}
      {deleteModalData !== null && (
        <Modal open onCancel={() => setDeleteModalData(null)} footer={false} closeIcon={<CloseIcon />} width={524} centered>
          <h4 className='text-center'>Delete linked {deleteModalData.type}</h4>
          <p>
            If you proceed, you will no longer be able to make purchases or transfer balances using this {deleteModalData.type} unless you re-link it.
            Are you sure you want to delete the following linked {deleteModalData.type}?
          </p>
          <div className='mb-32 modal-data'>
            {deleteModalData.logo}
            <span>{deleteModalData.name}</span>
            <span>{deleteModalData.number}</span>
          </div>
          <Row gutter={[16, 16]}>
            <Col span={24} sm={14}>
              <Button className='w-100' onClick={() => setDeleteModalData(null)}>
                Cancel
              </Button>
            </Col>
            <Col span={24} sm={10}>
              <Button className='w-100' type='primary' danger loading={deleteLoading} onClick={() => handleDeleteBankAccount()}>
                Delete
              </Button>
            </Col>
          </Row>
        </Modal>
      )}
      {/* Modal For Adding Bank */}
      <BankAccountModal
        bankModalOpen={bankModalOpen}
        setBankModalOpen={(value) => setBankModalOpen(value)}
        onSuccess={(value) => handleAddBankAccount(value)}
        handleUpdateBanksList={handleUpdateBanksList}
        linkTokenUnverified={linkTokenUnverified}
        bankVerificationStatus={verificationStatus}
        shouldUpdateList
      />
      {/* Modal For Adding Card */}
    </div>
  );
};
export default Payment;
